import { useState, useEffect } from "react";
import Modal from "../shared/Modal";
import { ModalMatchingEmployees } from "../personas/ModalMatchingEmployees";

import "./ModalEmployeeResourceSearch.css";
import {
  Employee,
  EmployeeRecord,
  FlattenedEmployeeRecord,
  getDefaultUserAvatar,
} from "winslow-core";
import { erGetCPValue } from "../app/app-employee-records/employeeRecordUtils";

interface ModalEmployeeResourceSearchProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (employeeId: string) => void;
  employees: (Employee | FlattenedEmployeeRecord)[];
}

export const ModalEmployeeResourceSearch: React.FC<
  ModalEmployeeResourceSearchProps
> = ({ isOpen, onClose, onSelect, employees }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showEmployeeList, setShowEmployeeList] = useState(false);

  // Helper function to get display name from Employee or EmployeeRecord
  const getDisplayName = (item: Employee | EmployeeRecord): string => {
    if ("workEmail" in item) {
      // EmployeeRecord
      const firstName = erGetCPValue(item, "system-firstName");
      const lastName = erGetCPValue(item, "system-lastName");
      return (
        [firstName, lastName].filter(Boolean).join(" ").trim() ||
        item.workEmail ||
        "Unknown"
      );
    } else {
      // Employee
      return item.displayName || item.email || "Unknown";
    }
  };

  // Helper function to get avatar
  const getAvatar = (item: Employee | EmployeeRecord): string => {
    if ("workEmail" in item) {
      // EmployeeRecord
      return item.avatarURL || getDefaultUserAvatar(getDisplayName(item));
    } else {
      // Employee
      return (
        item?.profile?.avatar ||
        getDefaultUserAvatar(item.displayName || item.email || "")
      );
    }
  };

  // Helper function to get title
  const getTitle = (item: Employee | EmployeeRecord): string => {
    if ("workEmail" in item) {
      // EmployeeRecord
      return erGetCPValue(item, "system-title") || "";
    } else {
      // Employee
      return item.title || "";
    }
  };

  const filteredEmployees = employees.filter((item) => {
    const displayName = getDisplayName(item);
    return displayName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscapeKey);
    return () => document.removeEventListener("keydown", handleEscapeKey);
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      style={{ width: "672px" }}
      canClose={true}
      escapeToClose={true}
      allowClickout={true}>
      <div className="employee-search-modal">
        <div className="employee-search-header">
          <div className="employee-search-title-row">
            <h1 className="employee-search-title">Employee Resource Search</h1>
            <div
              className="employee-count"
              onClick={() => setShowEmployeeList(true)}
              style={{ cursor: "pointer" }}>
              {employees.length === 0
                ? "No Employees"
                : employees.length === 1
                  ? "1 Employee"
                  : `${employees.length} Employees`}
            </div>
          </div>
          <div className="employee-search-container">
            <label htmlFor="employeeSearch" className="visually-hidden">
              Search employees
            </label>
            <input
              id="employeeSearch"
              type="text"
              className="employee-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search employees"
              aria-label="Search employees"
            />
            {searchTerm && filteredEmployees.length > 0 && (
              <div className="employee-results-container">
                {filteredEmployees.map((item, index) => (
                  <div
                    key={item.id}
                    className="employee-item"
                    onClick={() => {
                      onSelect(item.id);
                      onClose();
                    }}>
                    <img
                      loading="lazy"
                      src={getAvatar(item)}
                      className="employee-avatar"
                      alt={getDisplayName(item)}
                    />
                    <div className="employee-info">
                      <div className="employee-name">
                        {getDisplayName(item)}
                      </div>
                      <div className="employee-role">{getTitle(item)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <ModalMatchingEmployees
        isOpen={showEmployeeList}
        onClose={() => setShowEmployeeList(false)}
        employees={employees}
        title="All Employees"
      />
    </Modal>
  );
};
